import { fmtTime } from "../components/geotab/geotabFormat.js";

// Vue « Voir » d'un écart Geotab : la ligne du camion telle qu'au Dispatch et, dessous, sa journée vue par Geotab, sur la même
// échelle de temps. L'arrêt signalé clignote ; ce que le Dispatch prévoyait sans que Geotab le voie est estompé et marqué d'un ✕.

const SAME_MINUTE = 0.5;
const near = (a, b) => a != null && b != null && Math.abs(a - b) < SAME_MINUTE;

// Minute → % de la piste du Gantt : même calcul que le placement des segments (marges fixes de part et d'autre).
export function minuteToPercent(scale, minute) {
  const lead = Number(scale?.lead) || 0;
  const trail = Number(scale?.trail) || 0;
  const span = Number(scale?.span) || 0;
  if (!(span > 0)) return 0;
  const percent = lead + ((minute - (Number(scale.start) || 0)) / span) * (100 - lead - trail);
  return Math.max(0, Math.min(percent, 100));
}

// Sans ligne au Gantt (camion masqué ou sans tournée) : la journée réelle et prévue, avec une demi-heure de marge.
export function fallbackScale(day) {
  const minutes = [day?.real_departure, day?.real_return, ...(day?.visits || []).flatMap((v) => [v.arr, v.dep]),
    ...(day?.sites || []).flatMap((s) => [s.plan_arrival, s.plan_departure])].filter((m) => Number.isFinite(m));
  if (!minutes.length) return { start: 300, span: 840, lead: 0, trail: 0 };
  const start = Math.floor(Math.min(...minutes) / 60) * 60 - 30;
  return { start, span: Math.ceil(Math.max(...minutes) / 60) * 60 + 30 - start, lead: 0, trail: 0 };
}

function kindOf(visit, site, extraStop, returnAt) {
  if (extraStop) return extraStop.role === "loading" || extraStop.role === "delivery" ? extraStop.role : "stop";
  if (site) return site.kind === "loading" ? "loading" : "delivery";
  if (visit.kind === "loading") return "loading";
  if (visit.kind === "depot") return near(visit.arr, returnAt) ? "return" : "depot";
  return "stop";
}

const LABELS = { return: "Retour", depot: "Dépôt", stop: "Arrêt" };

// Numéros d'étapes d'un segment du Gantt, lus sur sa pastille (« 8-9 » → [8, 9]) ; null sans pastille.
export function segmentSteps(text) {
  const badge = String(text || "").trim().split(/\s*\n\s*/)[0];
  const match = badge.match(/^(\d+)(?:\s*[-–]\s*(\d+))?$/);
  return match ? [Number(match[1]), Number(match[2] || match[1])] : null;
}

// Segments de la ligne Geotab (de l'arrivée à l'arrivée suivante, comme au Gantt), liens avec le Dispatch et sites non vus.
export function buildGapScene(day, item) {
  const sites = day?.sites || [];
  const visits = [...(day?.visits || [])].sort((a, b) => a.arr - b.arr);
  const stops = item?.stops || [];
  const segments = [];
  if (day?.real_departure != null && visits.length && visits[0].arr > day.real_departure) {
    segments.push({ key: "start", kind: "start", label: "Départ", from: day.real_departure, to: visits[0].arr, extra: false });
  }
  visits.forEach((visit, index) => {
    const site = visit.site_idx != null ? sites.find((s) => s.idx === visit.site_idx) : null;
    const extraStop = stops.find((stop) => near(stop.arr, visit.arr)) || null;
    const kind = kindOf(visit, site, extraStop, day.real_return);
    const next = visits[index + 1];
    segments.push({
      key: `visit:${index}`, kind, extra: Boolean(extraStop), siteIdx: site?.idx ?? null,
      label: extraStop?.place || site?.name || visit.name || LABELS[kind] || "Arrêt",
      from: visit.arr, to: next && kind !== "return" ? next.arr : Math.max(visit.dep, visit.arr + 1),
      arr: visit.arr, dep: visit.dep,
    });
  });

  const matched = sites.filter((site) => site.real_arrival != null)
    .map((site) => ({ siteIdx: site.idx, steps: site.steps || [], planArrival: site.plan_arrival, segmentKey: segments.find((s) => s.siteIdx === site.idx || (s.siteIdx == null && near(s.from, site.real_arrival)))?.key }))
    .filter((link) => link.segmentKey);

  // Non vu : sauté si une étape suivante est faite (on le sait à son heure de départ prévue), sinon pas fait parce que le
  // camion est rentré (on le sait à son retour).
  const returnAt = day?.real_return;
  const missing = sites.filter((site) => site.real_arrival == null
    && (!day?.in_progress || sites.some((other) => other.idx > site.idx && other.real_arrival != null))).map((site) => {
    const later = sites.filter((other) => other.idx > site.idx && other.real_arrival != null);
    const after = later.length > 0;
    const rentre = !after && returnAt != null;
    return { siteIdx: site.idx, steps: site.steps || [], planArrival: site.plan_arrival,
      known: day?.in_progress ? Math.min(...later.map((other) => other.real_arrival))
        : rentre ? returnAt : Math.min(site.plan_departure ?? site.plan_arrival, returnAt ?? Infinity),
      label: rentre ? `Pas fait · camion rentré à ${fmtTime(returnAt)}` : "Pas fait" };
  });
  // Un libellé par suite de sites voisins non vus pour la même raison.
  const missingGroups = [];
  missing.forEach((entry) => {
    const last = missingGroups[missingGroups.length - 1];
    if (last && last.label === entry.label && last.sites[last.sites.length - 1].siteIdx === entry.siteIdx - 1) { last.sites.push(entry); last.known = Math.max(last.known, entry.known); }
    else missingGroups.push({ label: entry.label, known: entry.known, sites: [entry] });
  });
  // Différences avec le Prévu, dans l'ordre du temps : la relecture s'arrête sur chacune.
  const extraFrom = segments.find((segment) => segment.extra)?.from;
  const events = [
    ...missingGroups.filter((group) => Number.isFinite(group.known)).map((group) => ({ kind: "missed", t: group.known, group })),
    ...(extraFrom != null ? [{ kind: "extra", t: extraFrom }] : []),
  ].sort((a, b) => a.t - b.t);

  // Place de l'arrêt signalé dans l'ordre du Dispatch : après le dernier site fait avant lui, avant le premier fait après.
  const firstArr = stops.length ? Math.min(...stops.map((stop) => stop.arr)) : null;
  const done = sites.filter((site) => site.real_arrival != null);
  const before = firstArr == null ? null : done.filter((site) => site.real_arrival < firstArr).sort((a, b) => b.real_arrival - a.real_arrival)[0];
  const after = firstArr == null ? null : done.filter((site) => site.real_arrival > firstArr).sort((a, b) => a.real_arrival - b.real_arrival)[0];
  // Le point bleu du Gantt indique le site encore ouvert : la relecture s'arrête à son arrivée constatée,
  // sans prolonger l'arrêt jusqu'à un départ encore inconnu. Sans point bleu, dernier arrêt observé.
  const endAt = day?.in_progress
    ? sites.find((site) => site.open_end && site.real_arrival != null)?.real_arrival ?? visits.at(-1)?.arr
    : day?.real_return ?? segments.at(-1)?.to;
  return {
    segments, matched, missing, missingGroups, events, endAt,
    caret: { after: before ? { steps: before.steps || [], planArrival: before.plan_arrival } : null,
      before: after ? { steps: after.steps || [], planArrival: after.plan_arrival } : null },
  };
}
